import React from 'react';
import { AlertTriangle, Info, CheckCircle2, Trash2, X, RefreshCw } from 'lucide-react';

export type AlertVariant = 'destructive' | 'warning' | 'info' | 'success';

export interface AlertDialogProps {
  open: boolean;
  title: string;
  description: string | React.ReactNode;
  variant?: AlertVariant;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  singleButton?: boolean;
  children?: React.ReactNode;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({
  open,
  title,
  description,
  variant = 'info',
  confirmText = 'Onayla',
  cancelText = 'Vazgeç',
  onConfirm,
  onCancel,
  loading = false,
  singleButton = false,
  children,
}) => {
  if (!open) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'destructive':
        return {
          icon: <Trash2 className="w-6 h-6 text-red-600" />,
          iconBg: 'bg-red-50 border-red-200 text-red-600',
          confirmBtn: 'bg-red-600 hover:bg-red-700 text-white shadow-red-600/25',
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-6 h-6 text-amber-600" />,
          iconBg: 'bg-amber-50 border-amber-200 text-amber-600',
          confirmBtn: 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/25',
        };
      case 'success':
        return {
          icon: <CheckCircle2 className="w-6 h-6 text-emerald-600" />,
          iconBg: 'bg-emerald-50 border-emerald-200 text-emerald-600',
          confirmBtn: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/25',
        };
      case 'info':
      default:
        return {
          icon: <Info className="w-6 h-6 text-indigo-600" />,
          iconBg: 'bg-indigo-50 border-indigo-200 text-indigo-600',
          confirmBtn: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/25',
        };
    }
  };

  const currentStyles = getVariantStyles();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="alert-dialog-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/65 backdrop-blur-xs transition-opacity duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          onCancel();
        }
      }}
    >
      <div className="relative w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-2xl p-6 sm:p-7 text-slate-900 overflow-hidden transform transition-all animate-scaleIn">
        {/* Close Button */}
        {!loading && (
          <button
            type="button"
            onClick={onCancel}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Kapat"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Content Header */}
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-2xl border shrink-0 ${currentStyles.iconBg}`}>
            {currentStyles.icon}
          </div>
          <div className="space-y-1.5 min-w-0 flex-1">
            <h3
              id="alert-dialog-title"
              className="text-lg font-bold text-slate-900 tracking-tight leading-snug"
            >
              {title}
            </h3>
            <div className="text-sm text-slate-600 leading-relaxed font-normal">
              {description}
            </div>
          </div>
        </div>

        {/* Optional Custom Content */}
        {children && <div className="mt-4 pt-3 border-t border-slate-100">{children}</div>}

        {/* Footer Actions */}
        <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
          {!singleButton && (
            <button
              type="button"
              disabled={loading}
              onClick={onCancel}
              className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold transition disabled:opacity-50 cursor-pointer"
            >
              {cancelText}
            </button>
          )}

          <button
            type="button"
            disabled={loading}
            onClick={() => {
              if (onConfirm) onConfirm();
              else onCancel();
            }}
            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition disabled:opacity-50 cursor-pointer ${currentStyles.confirmBtn}`}
          >
            {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
            <span>{loading ? 'İşleniyor...' : confirmText}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
